import { useState, useEffect, useCallback } from 'react';
import { getAdminCommissionJobs } from '../../api/adminApi';
import { fmtIST } from '../../utils/dateFormat';

const cyan = '#00e5ff';
const TABS = ['all', 'pending', 'completed', 'failed'];

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  return isMobile;
};

const statusStyle = {
  pending:   { color: '#ffaa44', bg: 'rgba(255,170,68,0.12)', border: 'rgba(255,170,68,0.3)' },
  completed: { color: '#66ff99', bg: 'rgba(102,255,153,0.12)', border: 'rgba(102,255,153,0.3)' },
  failed:    { color: '#ff6666', bg: 'rgba(200,50,50,0.12)', border: 'rgba(200,50,50,0.3)' },
};

const AdminCommissions = () => {
  const [jobs, setJobs] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [tab, setTab] = useState('all');
  const [loading, setLoading] = useState(true);
  const isMobile = useIsMobile();

  const load = useCallback(() => {
    setLoading(true);
    getAdminCommissionJobs({ status: tab, page, limit: 20 })
      .then((res) => { setJobs(res.data.jobs); setTotal(res.data.total); setPages(res.data.pages); })
      .finally(() => setLoading(false));
  }, [tab, page]);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <h4 style={{ color: '#fff', fontWeight: 700, marginBottom: 4 }}>Commission Jobs</h4>
      <div style={{ color: '#555', fontSize: 13, marginBottom: 20 }}>{total} total records</div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {TABS.map((t) => (
          <button key={t} onClick={() => { setTab(t); setPage(1); }}
            style={{ background: tab === t ? cyan : 'rgba(255,255,255,0.05)', border: 'none', color: tab === t ? '#000' : '#888', borderRadius: 6, padding: '6px 16px', fontSize: 12, fontWeight: 600, cursor: 'pointer', textTransform: 'capitalize' }}>
            {t}
          </button>
        ))}
      </div>

      <div style={{ background: '#111', border: '1px solid rgba(0,229,255,0.1)', borderRadius: 12, overflow: isMobile ? 'auto' : 'hidden', overflowY: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: isMobile ? '750px' : '100%' }}>
          <thead>
            <tr style={{ background: 'rgba(0,229,255,0.07)' }}>
              {['User', 'Plan', 'Amount', 'Status', 'Attempts', 'Last Attempt', 'Error', 'Created'].map((h) => (
                <th key={h} style={{ color: cyan, fontSize: 12, fontWeight: 600, padding: '12px 14px', textAlign: 'left', whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} style={{ color: '#555', textAlign: 'center', padding: 32, fontSize: 13 }}>Loading...</td></tr>
            ) : jobs.length === 0 ? (
              <tr><td colSpan={8} style={{ color: '#555', textAlign: 'center', padding: 32, fontSize: 13 }}>No jobs found.</td></tr>
            ) : jobs.map((j) => {
              const ss = statusStyle[j.status] || statusStyle.pending;
              return (
                <tr key={j._id} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                  <td style={{ padding: '10px 14px' }}>
                    <div style={{ color: '#fff', fontSize: 12 }}>{j.userId?.name || '—'}</div>
                    <div style={{ color: '#444', fontSize: 10, fontFamily: 'monospace' }}>{j.userId?.userCode}</div>
                  </td>
                  <td style={{ color: '#aaa', fontSize: 12, padding: '10px 14px' }}>{j.planId?.planName || '—'}</td>
                  <td style={{ color: '#66ff99', fontWeight: 700, fontSize: 13, padding: '10px 14px' }}>{j.amount?.toFixed(2)} USDT</td>
                  <td style={{ padding: '10px 14px' }}>
                    <span style={{ background: ss.bg, border: `1px solid ${ss.border}`, color: ss.color, borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 600, textTransform: 'capitalize' }}>{j.status}</span>
                  </td>
                  <td style={{ color: '#888', fontSize: 12, padding: '10px 14px' }}>{j.attempts ?? 0}</td>
                  <td style={{ color: '#444', fontSize: 11, padding: '10px 14px', whiteSpace: 'nowrap' }}>{j.lastAttemptAt ? fmtIST(j.lastAttemptAt) : '—'}</td>
                  <td style={{ color: '#ff6666', fontSize: 11, padding: '10px 14px', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{j.error || '—'}</td>
                  <td style={{ color: '#444', fontSize: 11, padding: '10px 14px', whiteSpace: 'nowrap' }}>{fmtIST(j.createdAt)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {pages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 20 }}>
          {Array.from({ length: pages }, (_, i) => i + 1).map((p) => (
            <button key={p} onClick={() => setPage(p)}
              style={{ background: p === page ? cyan : 'rgba(255,255,255,0.06)', border: 'none', color: p === page ? '#000' : '#888', borderRadius: 6, padding: '6px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
              {p}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminCommissions;
